/**
 * Eine Belegung als gespannter Balken – dieselbe Mechanik wie die Buchungspille, samt Diagonale am
 * Wechseltag: bis zur Check-out-Zeit steht noch der alte Wagen da, danach gehört der Platz dem
 * Nächsten. Klick oder Tipp öffnet die Details, auch bei einer Sperre (am Handy als Blatt).
 */
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { BottomSheet } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { computePillLayout } from './BookingPill';
import { ParkingReservationDetails } from './ParkingReservationDetails';
import { PARKING_TONE, parkingState, spanLabel, type ParkingSpan } from './parkingCalendarHelpers';

interface Props {
  span: ParkingSpan;
  gridStart: number;
  mobile: boolean;
  /** Bei Dichte 14 trägt der Balken keinen Text mehr (Board „Belegung · Handy“). */
  showLabel: boolean;
}

export function ParkingReservationPill({ span, gridStart, mobile, showLabel }: Props) {
  const [open, setOpen] = useState(false);
  const layout = computePillLayout(span, gridStart);
  const label = spanLabel(span.entry);
  const hasDiagonalEdge = span.hasTransitionIn || span.hasTransitionOut;
  const bar = (
    <button
      type="button"
      aria-label={`${label}, Details`}
      title={label}
      // Am Desktop schaltet der Popover-Auslöser selbst.
      onClick={mobile ? () => setOpen(true) : undefined}
      className={cn(
        'flex w-full min-w-0 items-center overflow-hidden text-left text-xs font-bold',
        // Am Handy ist der Balken das Tippziel zu den Details – 44 px hoch.
        mobile ? 'h-11' : 'h-[34px]',
        PARKING_TONE[parkingState(span.entry)],
        !hasDiagonalEdge && 'rounded-[10px]',
      )}
      style={layout.clipPath ? { clipPath: layout.clipPath } : undefined}
    >
      <span
        className="truncate"
        style={{ paddingLeft: layout.contentPadStart, paddingRight: layout.contentPadEnd }}
      >
        {showLabel ? label : ''}
      </span>
    </button>
  );
  return (
    <div
      className="relative z-10 mx-0.5 flex h-full items-center"
      style={{ gridColumn: layout.gridColumn, gridRow: 1 }}
    >
      {mobile ? (
        <>
          {bar}
          <BottomSheet open={open} onOpenChange={setOpen} title="Stellplatz-Belegung">
            <div className="py-1">
              <ParkingReservationDetails entry={span.entry} />
            </div>
          </BottomSheet>
        </>
      ) : (
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>{bar}</Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="start"
              sideOffset={6}
              collisionPadding={16}
              aria-label="Stellplatz-Belegung"
              className="bg-card border-hairline shadow-panel rounded-tile z-50 w-[min(340px,calc(100vw-32px))] border p-4"
            >
              <ParkingReservationDetails entry={span.entry} />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
}
